import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { Link } from 'react-router-dom';
import {
    withStyles, Typography, Button,
} from '@material-ui/core';
import { bindActionCreators } from 'redux';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import * as PatientStore from '../../app/store/PatientStore';

import {
    ContentBox, TopActionBar, BottomActionBar, TaskName, VimoModal,
} from '../../common/components';
import TaskInfo from './components/TaskInfo';
import { SkillIcon } from '../../common/icons';
import { getLocalDate, today } from '../../youwell-common/dateUtils';
import SkillNavigation from './components/SkillNavigation';
import TrainingPlan from './components/TrainingPlan';
import DragableTask from './components/DragableTask';
import urls from '../../app/urls';
import TimePeriod from './components/TimePeriod';
import AboutTheExercises from '../program/components/AboutTheExercises';
import { vimoElementGroups } from '../../common/constants';
import { getMondayInWeek } from './components/utils';

const ALL_TASKS_DROP_ID = 'ALL_TASKS_DROP_ID';

const styles = theme => ({
    root: {

    },
    content: {
        padding: theme.spacing(4),
    },
    topLine: {
        marginTop: -theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
    },
    topContent: {
        marginBottom: theme.spacing(4),
        display: 'flex',
        justifyContent: 'space-between',
    },
    currentSkill: {
        width: 400,
        marginRight: theme.spacing(4),
    },
    skill: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.common.white,
    },
    skillIcon: {
        marginRight: theme.spacing(1),
    },
    timePeriod: {
        flex: '1 1 auto',
    },
    mainContent: {
        marginBottom: theme.spacing(2),
        display: 'flex',
    },
    calendar: {
        flex: '1 1 auto',
        marginRight: theme.spacing(2),
    },
    tasks: {
        width: 300,
    },
    description: {
        margin: theme.spacing(2),
    },

    allSkillsContent: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    task: {
        width: `calc(50% - ${theme.spacing(2)}px)`,
        margin: theme.spacing(1),
    },
    aboutTasks: {
        margin: theme.spacing(3),
    },
    aboutTasksHeader: {
        margin: theme.spacing(1),
        marginBottom: theme.spacing(4),
    },
    noSkillsDescription: {
        marginBottom: theme.spacing(2),
    },
    noSkillsButtons: {
        display: 'flex',
        justifyContent: 'center',
    },
});

const initialState = {
    studentId: '',
    updated: '',
    selectedSkills: [],
    hasUnsavedChanges: false,
    saveMessage: '',
    skillIndex: 0,
};

const getStateFromData = (studentData, skillNr) => {
    if (!studentData || !studentData.programData) {
        return {
            ...initialState,
            studentId: studentData && studentData.id,
            updated: studentData && studentData.updated,
        };
    }

    let skillIndex = Number(skillNr);

    if (Number.isNaN(skillIndex)) {
        const activeModule = studentData.programData.elements
            ?.findIndex(d => d.elementGroup === vimoElementGroups.main && getLocalDate(d.startTime) <= new Date() && (!d.endTime || getLocalDate(d.endTime) > today()))
            || 0;

        skillIndex = Number(activeModule) || 0;
    }

    return {
        completeDetailsLoaded: studentData.programData && !studentData.programData.limitedDetails,
        studentId: studentData.id,
        updated: studentData.updated,
        selectedSkills: studentData.programData.elements?.filter(d => d.elementGroup === vimoElementGroups.main) || [],
        hasUnsavedChanges: false,
        saveMessage: '',
        showModal: null,
        skillIndex,
    };
};

class Plan extends React.Component {
    state = getStateFromData(this.props.studentData, this.props.skillNr)

    static getDerivedStateFromProps(props, state) {
        if (props.studentData && (props.studentData.id !== state.studentId
            || props.studentData.updated !== state.updated
            || Number(props.skillNr) !== state.skillIndex
            || (props.studentData.programData && !state.completeDetailsLoaded && !props.studentData.programData.limitedDetails))) {
            return getStateFromData(props.studentData, props.skillNr);
        }
        return null;
    }

    showModal = task => () => {
        this.setState({
            showModal: task,
        });
    }

    closeModal = () => {
        this.setState({
            showModal: null,
        });
    }

    handleElementValuesChange = index => (values) => {
        this.setState(prevState => ({
            selectedSkills: prevState
                .selectedSkills
                .map((e, i) => (i === index
                    ? {
                        ...e,
                        ...values,
                    }
                    : e)),
            hasUnsavedChanges: true,
            saveMessage: '',
        }));
    }

    onDragEnd = (result) => {
        const { source, destination, draggableId } = result;

        const currentTasks = (this.state.selectedSkills[this.state.skillIndex] && this.state.selectedSkills[this.state.skillIndex].moduleSchedule
        && this.state.selectedSkills[this.state.skillIndex].moduleSchedule.tasks) || [];
        let tasks;

        if (!destination) {
            if (source.droppableId !== ALL_TASKS_DROP_ID) {
                // Remove element
                tasks = currentTasks.filter(d => draggableId !== `${new Date(d.date).toISOString()}-${d.taskId}`);
            }
        } else if (destination.droppableId !== ALL_TASKS_DROP_ID) {
            const tasksByDate = currentTasks.reduce((prev, curr) => (
                {
                    ...prev,
                    [new Date(curr.date).toISOString()]: [...prev[new Date(curr.date).toISOString()] || [], curr],
                }
            ), { [destination.droppableId]: [] });

            if (source.droppableId === ALL_TASKS_DROP_ID) {
                // Add element

                if (currentTasks.filter(d => d.taskId === draggableId && new Date(d.date).toISOString() === destination.droppableId).length === 0) {
                    const newTask = {
                        taskId: draggableId,
                        date: destination.droppableId,
                    };
                    const reordered = Array.from(tasksByDate[destination.droppableId]);
                    reordered.splice(destination.index, 0, newTask);

                    tasks = Object.keys(tasksByDate).reduce((prev, curr) => [...prev,
                        ...curr === destination.droppableId ? reordered : tasksByDate[curr],
                    ], []);
                }
            } else if (source.droppableId !== destination.droppableId) {
                // Move between days

                if (currentTasks.filter(d => d.taskId === draggableId.slice(source.droppableId.length + 1) && new Date(d.date).toISOString() === destination.droppableId).length === 0) {
                    const reorderedSource = Array.from(tasksByDate[source.droppableId]);
                    const [removed] = reorderedSource.splice(source.index, 1);

                    const reorderedDestination = Array.from(tasksByDate[destination.droppableId]);
                    reorderedDestination.splice(destination.index, 0, { ...removed, date: destination.droppableId });

                    tasks = Object.keys(tasksByDate).reduce((prev, curr) => {
                        if (curr === source.droppableId) {
                            return [...prev, ...reorderedSource];
                        }
                        if (curr === destination.droppableId) {
                            return [...prev, ...reorderedDestination];
                        }
                        return [...prev, ...tasksByDate[curr]];
                    }, []);
                }
            } else if (source.droppableId === destination.droppableId) {
                // Move within day

                const reordered = Array.from(tasksByDate[destination.droppableId]);
                const [removed] = reordered.splice(source.index, 1);
                reordered.splice(destination.index, 0, removed);

                tasks = Object.keys(tasksByDate).reduce((prev, curr) => [...prev,
                    ...curr === destination.droppableId ? reordered : tasksByDate[curr],
                ], []);
            }
        }

        if (tasks) {
            this.handleElementValuesChange(this.state.skillIndex)({
                moduleSchedule: {
                    tasks,
                },
            });
        }
    }

    save = () => {
        const { programData } = this.props.studentData;

        const patientProgram = {
            ...programData,
            elements: [
                ...programData.elements.filter(d => d.elementGroup !== vimoElementGroups.main),
                ...this.state.selectedSkills,
            ],
        };

        // If a task is outside the timespace of the skill, it is removed from the task schedule
        patientProgram.elements.forEach((el, index) => {
            if (el.moduleSchedule?.tasks?.length > 0) {
                const startMonday = getMondayInWeek(getLocalDate(el.startTime));
                const onlyTasksWithinDates = el.moduleSchedule.tasks
                    .filter(sche => getLocalDate(sche.date) >= startMonday && getLocalDate(sche.date) <= getLocalDate(el.endTime));

                if (!patientProgram.elements[index].moduleSchedule) {
                    patientProgram.elements[index].moduleSchedule = {};
                }
                patientProgram.elements[index].moduleSchedule.tasks = onlyTasksWithinDates;
            }
        });

        this.props.updateProgramForPatient(patientProgram, {
            callback: () => {
                this.setState({
                    hasUnsavedChanges: false,
                    saveMessage: this.props.translate('studentPlan.allChangesSaved'),
                });
            },
            redirect: `${urls.inside.student.home}/${this.props.studentData.id}`,
            reducerParams: {
                patientId: this.props.studentData.id,
            },
        });
    }

    render() {
        const {
            classes, translate, studentData, profileData,
        } = this.props;

        if (!studentData) {
            return null;
        }

        const currentSkill = this.state.selectedSkills[this.state.skillIndex];
        const canEdit = profileData?.userAccess?.editPatients;

        return (
            <div className={classes.root}>
                <TopActionBar
                    title={translate('studentPlan.planSkills')}
                    saveEnabled={this.state.hasUnsavedChanges}
                    onSave={this.save}
                />

                <div className={classes.content}>
                    {currentSkill
                        ? (
                            <>
                                <div className={classes.topLine}>
                                    <SkillNavigation
                                        selectedSkills={this.state.selectedSkills}
                                        studentId={studentData.id}
                                        currentSkillNr={this.state.skillIndex}
                                        disabled={!canEdit}
                                    />
                                </div>
                                <div className={classes.topContent}>
                                    <ContentBox
                                        className={classes.currentSkill}
                                        heading={translate('studentPlan.skill')}
                                        variant="filled"
                                    >
                                        <div className={classes.skill}>
                                            <SkillIcon className={classes.skillIcon} color="inherit" />
                                            <Typography variant="h5" color="inherit">
                                                {currentSkill.module ? currentSkill.module.name : ''}
                                            </Typography>
                                        </div>
                                    </ContentBox>
                                    <ContentBox
                                        className={classes.timePeriod}
                                        heading={translate('studentPlan.timePeriod')}
                                        variant={currentSkill.startTime ? 'bordered' : 'dashed'}
                                    >
                                        <TimePeriod
                                            skill={currentSkill}
                                            onChange={this.handleElementValuesChange(this.state.skillIndex)}
                                            disabled={!canEdit}
                                        />
                                    </ContentBox>
                                </div>
                                <div className={classes.mainContent}>
                                    <DragDropContext onDragEnd={this.onDragEnd}>
                                        <ContentBox
                                            className={classes.calendar}
                                            heading={translate('studentPlan.trainingPlan')}
                                            variant={currentSkill.startTime ? 'dashed' : null}
                                        >
                                            {currentSkill.startTime ? (
                                                <>
                                                    <TrainingPlan
                                                        skill={currentSkill}
                                                        onChange={this.handleElementValuesChange(this.state.skillIndex)}
                                                        availableDays={studentData?.extendedProperties?.availableWeekdays?.split(',') || []}
                                                        disabled={!canEdit}
                                                    />
                                                    <Typography variant="body2" className={classes.description} color="primary">
                                                        {translate('studentPlan.planDescription')}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography variant="body2" className={classes.description} color="primary">
                                                    {translate('studentPlan.planDescriptionStart')}
                                                </Typography>
                                            )}

                                        </ContentBox>
                                        <ContentBox
                                            className={classes.tasks}
                                            heading={translate('studentPlan.exercises')}
                                        >
                                            {currentSkill.startTime && (
                                                <Typography variant="body2" className={classes.description} color="primary">
                                                    {translate('studentPlan.planDescriptionDragItems')}
                                                </Typography>
                                            )}
                                            <Droppable droppableId={ALL_TASKS_DROP_ID} isDropDisabled>
                                                {(provided, snapshot) => (
                                                    <div
                                                        className={classes.allSkillsContent}
                                                        ref={provided.innerRef}
                                                        style={{ background: snapshot.isDraggingOver ? 'white' : '' }}
                                                        {...provided.droppableProps}
                                                    >
                                                        {currentSkill?.module?.tasks?.map((task, i) => (
                                                            <DragableTask
                                                                key={task.id}
                                                                task={task}
                                                                index={i}
                                                                className={classes.task}
                                                                onClick={this.showModal(task)}
                                                                disabled={!canEdit}
                                                            />
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </ContentBox>
                                    </DragDropContext>
                                </div>
                            </>
                        )
                        : (
                            <div className={classes.noSkills}>
                                <Typography variant="h5" gutterBottom>
                                    {translate('studentPlan.noSkillsSelected')}
                                </Typography>
                                <Typography className={classes.noSkillsDescription}>
                                    {translate('studentPlan.noSkillsSelectedDescription')}
                                </Typography>
                                <div className={classes.noSkillsButtons}>
                                    <Button
                                        component={Link}
                                        to={`${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.skills}`}
                                        variant="outlined"
                                        color="primary"
                                    >
                                        {translate('studentPlan.selectSkills')}
                                    </Button>
                                </div>
                            </div>
                        )}
                </div>

                {canEdit && (
                    <BottomActionBar
                        cancelLink={`${urls.inside.student.home}/${studentData.id}`}
                        onSave={this.save}
                        saveEnabled={this.state.hasUnsavedChanges}
                        saveMessage={this.state.saveMessage}
                    />
                )}

                <div className={classes.aboutTasks}>
                    <Typography variant="h4" className={classes.aboutTasksHeader}>
                        {translate('studentPlan.aboutTheExercises')}
                    </Typography>

                    <AboutTheExercises />
                </div>

                {this.state.showModal && (
                    <VimoModal
                        open
                        onClose={this.closeModal}
                        heading={(
                            <TaskName task={this.state.showModal} />
                        )}
                    >
                        <TaskInfo taskId={this.state.showModal.id} />
                    </VimoModal>
                )}

            </div>
        );
    }
}

Plan.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    updateProgramForPatient: PropTypes.func.isRequired,
    studentData: PropTypes.object,
    skillNr: PropTypes.string,
    profileData: PropTypes.object,
};

Plan.defaultProps = {
    studentData: null,
    skillNr: null,
    profileData: null,
};

const mapStateToProps = (state, ownProps) => ({
    translate: getTranslate(state.localize),
    profileData: state.auth.profileData,
    studentData: state.patient.details && state.patient.details[ownProps.match.params.studentId],
    skillNr: ownProps.match.params.skillNr,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(PatientStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Plan));
